"use client"

import { FragmentType, graphql } from "@/gql"
import { PortfolioClientPage, PortfolioClientPageFragment } from "../page.client"
import { PortfolioSearchParams, PortfolioUIState } from "../page"
import { PortfolioStaticDataQuery, ServerPortfolioQuery } from "@/gql/graphql"
import { NetworkStatus, useQuery } from "@apollo/client"
import React, { useEffect, useMemo, useState } from "react"
import { PageScrollWrapper } from "@/components/PageScrollWrapper"
import type { NovaSession } from "@/types"
import { useSearchParams } from "next/navigation"
import { EditingBanner } from "./edit/EditingBanner"
import { PortfolioEditModal, useEditPortfolio } from "./edit/useEditPortfolio"

const ClientPortfolioQuery = graphql(`
  query ClientPortfolio(
    $handle: String!
    $first: Int
    $after: String
    $creatorRoles: [ID!]
    $categories: [ID!]
    $displayingPrivateProfile: Boolean!
    $isCreativeViewer: Boolean!
  ) {
    creativeByHandle(handle: $handle) {
      id
      __typename
      ...PortfolioClientPage
    }
  }
`)

interface Props {
  params: { handle: string }
  searchParams: PortfolioSearchParams
  uiState: PortfolioUIState
  staticData: PortfolioStaticDataQuery
  serverSession: NovaSession
  serverData: ServerPortfolioQuery
}

export const ClientQueryWrapper: React.FC<Props> = ({
  params,
  searchParams,
  uiState,
  staticData,
  serverSession,
  serverData,
}) => {
  const search = useSearchParams()
  const editProfileMethods = useEditPortfolio(uiState)

  // State to track the initial search params
  const [initialParams, setInitialParams] = useState({
    creatorRoles: search?.get("role")?.split(",") || [],
    categories: search?.get("type")?.split(",") || [],
  })

  // Memoized current search params
  const currentParams = useMemo(
    () => ({
      creatorRoles: search?.get("role")?.split(",") || [],
      categories: search?.get("type")?.split(",") || [],
    }),
    [search]
  )

  const {
    data: clientData,
    fetchMore,
    loading,
    refetch,
    networkStatus,
  } = useQuery(ClientPortfolioQuery, {
    variables: {
      handle: params.handle,
      creatorRoles: initialParams.creatorRoles,
      categories: initialParams.categories,
      first: 12,
      displayingPrivateProfile: false,
      isCreativeViewer: serverSession?.account === "creative",
    },
    notifyOnNetworkStatusChange: true,
  })

  const isFetchingFreshData = loading && networkStatus !== NetworkStatus.fetchMore

  // Effect to refetch data only when params change
  useEffect(() => {
    const paramsChanged =
      JSON.stringify(currentParams.creatorRoles) !== JSON.stringify(initialParams.creatorRoles) ||
      JSON.stringify(currentParams.categories) !== JSON.stringify(initialParams.categories)

    if (paramsChanged) {
      setInitialParams(currentParams)
      refetch({
        handle: params.handle,
        creatorRoles: currentParams.creatorRoles,
        categories: currentParams.categories,
        first: 12,
        displayingPrivateProfile: false,
        isCreativeViewer: serverSession?.account === "creative",
      })
    }
  }, [currentParams, initialParams, params.handle, refetch, serverSession])

  return (
    <>
      <EditingBanner isEditing={editProfileMethods.isEditing} toggleEdit={editProfileMethods.toggleEdit} />
      <PageScrollWrapper>
        <PortfolioClientPage
          clientCreative={
            clientData?.creativeByHandle?.__typename === "IndividualCreative" ? clientData?.creativeByHandle : null
          }
          serverCreative={
            serverData?.individualCreative?.__typename === "IndividualCreative"
              ? (serverData.individualCreative as FragmentType<typeof PortfolioClientPageFragment>)
              : null
          }
          params={params}
          searchParams={searchParams}
          uiState={uiState}
          staticData={staticData}
          editProfileMethods={editProfileMethods}
          serverSession={serverSession}
          fetchMoreClientSide={fetchMore}
          isFetchingFreshClientSideData={isFetchingFreshData}
        />
      </PageScrollWrapper>
    </>
  )
}
